import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EASE } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";

const Panel = ({ c, big }) => (
  <>
    <div>
      <span
        className={"font-serif text-copper block leading-[1.15] " + (big ? "text-6xl lg:text-7xl" : "text-5xl")}
        data-testid={`manifesto-number-${c.key}`}
      >
        {c.n}
      </span>
      <span className="block h-px w-16 bg-copper mt-4" />
    </div>
    <div className="mt-6">
      <h3 className={"font-serif text-cream " + (big ? "text-3xl lg:text-4xl" : "text-2xl")}>{c.title}</h3>
      <p className="mt-4 text-cream2 leading-relaxed text-sm sm:text-base">{c.text}</p>
    </div>
  </>
);

export default function ManifestoCarousel({ chapters }) {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const current = chapters[active] || chapters[0];

  // Mobile: il pannello attivo segue lo swipe orizzontale
  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActive(Math.max(0, Math.min(chapters.length - 1, idx)));
  };

  const goTo = useCallback(
    (i) => {
      const next = (i + chapters.length) % chapters.length;
      setActive(next);
      const el = trackRef.current;
      if (el) el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
    },
    [chapters.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goTo(active + 1);
      if (e.key === "ArrowLeft") goTo(active - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, goTo]);

  if (!chapters.length) return null;

  return (
    <div data-testid="manifesto-carousel">
      {/* ---------- MOBILE / TABLET: carosello a swipe ---------- */}
      <div className="lg:hidden">
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="flex snap-x snap-mandatory overflow-x-auto no-scrollbar -mx-5 px-5 gap-4"
          data-testid="manifesto-track"
        >
          {chapters.map((c) => (
            <article
              key={c.n}
              className="snap-center shrink-0 w-full border border-copper/20 bg-obsidian2/60 p-7 sm:p-9"
              data-testid={`manifesto-chapter-${c.key}`}
            >
              <Panel c={c} />
            </article>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="manifesto-dots">
            {chapters.map((c, i) => (
              <button
                key={c.n}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Vai al principio ${c.n}`}
                className={"h-1.5 rounded-full transition-all duration-300 " + (i === active ? "w-8 bg-copper" : "w-3 bg-copper/30")}
                data-testid={`manifesto-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] tracking-[0.2em] text-sand" data-testid="manifesto-counter">
              {current.n} / 0{chapters.length}
            </span>
            <button
              type="button"
              onClick={() => goTo(active - 1)}
              className="h-9 w-9 flex items-center justify-center border border-copper/30 text-copperlight active:bg-copper/15"
              aria-label="Principio precedente"
              data-testid="manifesto-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => goTo(active + 1)}
              className="h-9 w-9 flex items-center justify-center border border-copper/30 text-copperlight active:bg-copper/15"
              aria-label="Principio successivo"
              data-testid="manifesto-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- DESKTOP: pannello in primo piano + raccoglitore ---------- */}
      <div className="hidden lg:grid grid-cols-12 gap-10 items-start">
        <div className="col-span-7 relative border border-copper/25 bg-obsidian2/60 p-12 min-h-[26rem] flex flex-col justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.n}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: EASE }}
              data-testid={`manifesto-active-${current.key}`}
            >
              <Panel c={current} big />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="col-span-5 pt-2" data-testid="manifesto-stack">
          <p className="eyebrow mb-5">{t("manifesto.others")}</p>
          {chapters.map((c, i) =>
            i === active ? null : (
              <button
                key={c.n}
                type="button"
                onClick={() => setActive(i)}
                className="group relative block w-full text-left border border-copper/20 bg-obsidian px-7 py-6 -mt-3 first:mt-0 hover:-translate-y-1.5 hover:border-copper/50 hover:bg-obsidian2 transition-[transform,border-color,background-color] duration-300"
                style={{ zIndex: i + 1 }}
                data-testid={`manifesto-stack-item-${c.key}`}
              >
                <span className="font-serif text-2xl text-copper/80 group-hover:text-copper transition-colors">{c.n}</span>
                <span className="block font-serif text-xl text-cream mt-1">{c.title}</span>
                <span className="block mt-2 text-sm text-cream2/70 line-clamp-2">{c.text}</span>
                <ChevronRight className="absolute right-5 top-1/2 -translate-y-1/2 h-4 w-4 text-copper opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
